import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient.ts';
import { offlineAuthService } from '../lib/offlineAuth.ts';
import { useOnlineStatus } from '../hooks/useOnlineStatus.ts';

interface User {
  id: string;
  email: string;
  name: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isOfflineMode: boolean;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: Error | null }>;
  signUp: (email: string, password: string, name: string) => Promise<{ user: User | null; error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const isOnline = useOnlineStatus();

  const signIn = async (email: string, password: string) => {
    // If offline, try offline authentication
    if (!isOnline) {
      try {
        const result = await offlineAuthService.attemptOfflineLogin(email, password);
        if (result.success && result.user) {
          const userData: User = {
            id: result.user.id,
            email: result.user.email || email,
            name: result.user.user_metadata?.full_name || result.user.user_metadata?.display_name || result.user.user_metadata?.name || 'User',
            created_at: result.user.created_at
          };
          // Store user in localStorage for session restoration
          localStorage.setItem('agrosense_user', JSON.stringify(userData));
          
          setUser(userData);
          setIsOfflineMode(true);
          return { user: userData, error: null };
        }
        return { user: null, error: new Error('No offline credentials found') };
      } catch (error) {
        return { user: null, error: error as Error };
      }
    }

    // Online - try Supabase
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { user: null, error };
      
      if (data.session?.user) {
        const userData: User = {
          id: data.user.id,
          email: data.user.email || email,
          name: data.user.user_metadata?.full_name || data.user.user_metadata?.display_name || data.user.user_metadata?.name || 'User',
          created_at: data.user.created_at
        };
        
        // Store credentials for offline use
        try {
          console.log('Storing offline credentials for user:', data.user.email);
          await offlineAuthService.storeCredentialsAfterLogin(data.user, password, data.session);
          console.log('Offline credentials stored successfully');
        } catch (offlineError) {
          console.error('Failed to store offline credentials:', offlineError);
        }
        
        // Store user in localStorage for session restoration
        localStorage.setItem('agrosense_user', JSON.stringify(userData));
        
        setUser(userData);
        setIsOfflineMode(false);
        return { user: userData, error: null };
      }
      
      return { user: null, error: new Error('Sign in failed') };
    } catch (error) {
      return { user: null, error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    if (!isOnline) {
      return { user: null, error: new Error('Internet connection required for registration') };
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name } }
      });

      if (error) return { user: null, error };

      if (data.session?.user) {
        const userData: User = {
          id: data.user.id,
          email: data.user.email || email,
          name: name,
          created_at: data.user.created_at
        };
        setUser(userData);
        setIsOfflineMode(false);
        return { user: userData, error: null };
      }

      return { user: null, error: new Error('Registration successful. Please check your email.') };
    } catch (error) {
      return { user: null, error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      if (isOnline) {
        await supabase.auth.signOut();
      }
      // DO NOT clear any data - preserve everything for offline access
      // Only clear current session state
      setUser(null);
      setIsOfflineMode(!isOnline);
    } catch (error) {
      console.warn('Sign out error:', error);
      setUser(null);
      setIsOfflineMode(!isOnline);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (isOnline) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            const userData: User = {
              id: session.user.id,
              email: session.user.email || '',
              name: session.user.user_metadata?.full_name || session.user.user_metadata?.display_name || session.user.user_metadata?.name || 'User',
              created_at: session.user.created_at
            };
            setUser(userData);
            setIsOfflineMode(false);
            setLoading(false);
            return;
          }
        }

        // Check for stored offline user
        const storedUser = localStorage.getItem('agrosense_user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            setIsOfflineMode(true);
          } catch (e) {
            console.warn('Failed to parse stored user:', e);
          }
        }
      } catch (error) {
        console.warn('Auth initialization failed:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [isOnline]);

  useEffect(() => {
    if (!isOnline) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, session: any) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const userData: User = {
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.display_name || session.user.user_metadata?.name || 'User',
            created_at: session.user.created_at
          };
          
          setUser(userData);
          setIsOfflineMode(false);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setIsOfflineMode(!isOnline);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [isOnline]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isOfflineMode,
      signIn,
      signUp,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}